import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Spinner } from "react-bootstrap";
import { UserPlus, Users } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../../../redux/slices/userSlice";
import {
  deleteProjectMember,
  fetchProjectMembers,
} from "../../../redux/slices/projectsSlice";
import AddProjectMembersModal from "../../Modal/users/AddProjectMembersModal/AddProjectMembersModal";
import ProjectMemberItem from "../ProjectMemberItem/ProjectMemberItem";
import "../../projects/projectsFeature.css";
import i18n from "../../../i18n";

const ProjectMembers = ({ project: projectProp = null }) => {
  const dispatch = useDispatch();

  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [isCreate, setIsCreate] = useState(true);

  const { items: users = [] } = useSelector((state) => state.users);
  const { item: storeProject, isLoading } = useSelector((state) => state.projects);

  const project = projectProp || storeProject;
  const projectId = project?.id;

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectMembers(projectId));
    }
  }, [dispatch, projectId]);

  const members = useMemo(() => {
    const sourceMembers = project?.members || [];

    return [...sourceMembers].sort((left, right) => {
      const leftName = `${left?.user?.first_name || ""} ${left?.user?.last_name || ""}`.trim();
      const rightName = `${right?.user?.first_name || ""} ${right?.user?.last_name || ""}`.trim();
      return leftName.localeCompare(rightName);
    });
  }, [project?.members]);

  const handleOpenCreate = () => {
    setSelectedMember(null);
    setIsCreate(true);
    setShowAddUsersModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setIsCreate(false);
    setShowAddUsersModal(true);
  };

  const handleRemoveMember = async (member) => {
    if (!projectId || !member?.user_id) {
      return;
    }

    const confirmed = window.confirm(i18n.t("components.ProjectMembers.confirm_remove"));
    if (!confirmed) {
      return;
    }

    setRemovingUserId(member.user_id);
    const result = await dispatch(
      deleteProjectMember({
        projectId,
        userId: member.user_id,
      })
    );

    if (!result.error) {
      await dispatch(fetchProjectMembers(projectId));
    }

    setRemovingUserId(null);
  };

  const handleModalSaved = async () => {
    if (!projectId) {
      return;
    }

    await dispatch(fetchProjectMembers(projectId));
    setSelectedMember(null);
  };

  if (!projectId) {
    return <p className="text-muted mb-0">{i18n.t("components.ProjectMembers.no_project_selected")}</p>;
  }

  return (
    <div className="project-members-panel">
      <div className="project-members-header mb-3">
        <div className="d-flex align-items-center gap-2">
          <Users size={18} />
          <h5 className="mb-0">{i18n.t("components.ProjectMembers.project_members")}</h5>
          <Badge bg="secondary">{members.length}</Badge>
        </div>

        <Button className="project-members-add-btn" onClick={handleOpenCreate}>
          <UserPlus size={16} />
          <span>{i18n.t("components.ProjectMembers.manage_members")}</span>
        </Button>
      </div>

      <AddProjectMembersModal
        project={project}
        users={users}
        showModal={showAddUsersModal}
        setShowModal={setShowAddUsersModal}
        editProjectMember={selectedMember}
        onSaved={handleModalSaved}
        isCreate={isCreate}
      />

      {isLoading && members.length === 0 ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : members.length > 0 ? (
        <div className="project-members-list">
          {members.map((member, index) => (
            <ProjectMemberItem
              key={`${member?.user_id || index}-${member?.start_date || "active"}`}
              member={member}
              onEdit={handleEditMember}
              onRemove={handleRemoveMember}
              isRemoving={removingUserId === member?.user_id}
            />
          ))}
        </div>
      ) : (
        <Card className="project-members-empty">
          <Card.Body className="text-center">
            <h6 className="mb-1">{i18n.t("components.ProjectMembers.no_members_assigned")}</h6>
            <p className="text-muted mb-0">{i18n.t("components.ProjectMembers.assign_members_and_etis_project")}</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ProjectMembers;
